// react
import React, { useEffect, useState } from "react";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// react-copy-to-clipboard
import { CopyToClipboard } from "react-copy-to-clipboard";

import { Box, Grid, Tooltip, Button, IconButton, Typography, TextField, Divider } from "@mui/material";
import {
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  IosShare as IosShareIcon,
  DeleteForever as DeleteForeverIcon,
  Add as AddIcon,
  SaveAlt as SaveAltIcon,
} from "@mui/icons-material";

// features/user
import getPubKeyUrl from "features/user/getPubKeyUrl";
import addPubKeyUrl from "features/user/addPubKeyUrl";

// A ---------------------------------------------------------------------- M

const SigningKeysCard = (props) => {
  const { user, seedflow, pubKey, canSign } = UserAuth();
  const { t } = useTranslation();

  const [pbkurl, setPbkurl] = useState("");
  const [copied, setCopied] = useState(false);
  const [editable, setEditable] = useState(false);

  useEffect(() => {
    const load = async () => {
      const pbkurl = await getPubKeyUrl(user.uid);
      setPbkurl(pbkurl);
    };
    load();
  }, [user]);

  const handleCopy = () => {
    setCopied(true);
  };

  const handleSave = async () => {
    if (pbkurl) await addPubKeyUrl(user.uid, pbkurl);
    setEditable(false);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          {t("identity_access_management")}
        </Typography>
        <Divider sx={{ mb: 2, opacity: 0.4 }} />
      </Grid>

      <Grid item container spacing={3}>
        {seedflow && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("public_key")}
                value={pubKey}
                margin="dense"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <CopyToClipboard text={pubKey} onCopy={handleCopy}>
                      <Tooltip title={copied ? t("copied") : t("copy")} placement="top">
                        <IconButton size="small" color="primary">
                          <ContentCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CopyToClipboard>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pub_key_url")}
                value={pbkurl}
                onChange={(e) => setPbkurl(e.target.value)}
                disabled={!editable}
                margin="dense"
                InputProps={{
                  endAdornment: (
                    <Tooltip title={editable ? t("save") : t("edit")} placement="top">
                      <IconButton size="small" color="primary" onClick={editable ? handleSave : () => setEditable(true)}>
                        {editable ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  mt: 1,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<IosShareIcon />}
                  onClick={props.handleOpenPasswordExport}
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    px: 2,
                  }}
                >
                  {t("export_secret")}
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteForeverIcon />}
                  onClick={props.handleOpenForget}
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    px: 2,
                  }}
                >
                  {t("forget_secret")}
                </Button>
              </Box>
            </Grid>
          </>
        )}

        {canSign && !seedflow && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={props.handleOpenPasswordGenerate}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  px: 2,
                }}
              >
                {t("generate_secret")}
              </Button>

              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveAltIcon />}
                onClick={props.handleOpenPasswordImport}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  px: 2,
                }}
              >
                {t("import_secret")}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default SigningKeysCard;
